/**
 * Instantiates a SceneResource into a THREE object tree and runs node scripts.
 * Mirrors Godot: enter_tree → ready (children first) → process loop.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { SceneNodeData, SceneResource, NodeProps } from './types';
import { uid } from './types';
import {
  bindScriptInstance,
  callLifecycle,
  callProcess,
  compileScript,
  createNodeAPI,
  parseSignals,
  type LiveScriptInstance,
  type ScriptHost,
  type TreeNodeLink,
} from './ScriptRuntime';
import {
  makeBladeMaterial,
  makeBreechesMaterial,
  makeFloorMaterial,
  makeGloveMaterial,
  makeGripMaterial,
  makeGuardMaterial,
  makeJacketMaterial,
  makeLameMaterial,
  makeMaskBibMaterial,
  makeMaskShellMaterial,
  makePisteMetal,
  makePisteWood,
  makeShoeMaterial,
  makeSockMaterial,
} from '../materials';
import { loadSceneByPath } from './storage';
import { cloneNode } from './SceneDocument';
import { SceneTree } from './SceneTree';
import { SignalBus } from './Signal';
import { Input } from './InputMap';
import {
  collectColliders,
  resolveStaticCollisions,
  updateAreaOverlaps,
} from './Physics';
import {
  defaultAnimLibrary,
  parseAnimLibrary,
  sampleTrack,
  type AnimClipData,
  type AnimLibrary,
  type AnimProperty,
} from './AnimData';

/** Deep-clone a packed subtree with fresh ids so multiple instances don't collide. */
function rekeyPacked(node: SceneNodeData, idMap: Map<string, string>): SceneNodeData {
  const copy = cloneNode(node);
  const walk = (n: SceneNodeData) => {
    const old = n.id;
    n.id = uid('inst');
    idMap.set(old, n.id);
    for (const c of n.children) walk(c);
  };
  walk(copy);
  return copy;
}

const gltfCache = new Map<string, THREE.Group>();

async function loadModel(url: string): Promise<THREE.Object3D> {
  const hit = gltfCache.get(url);
  if (hit) return SkeletonUtils.clone(hit);
  const gltf = await new GLTFLoader().loadAsync(url);
  gltfCache.set(url, gltf.scene);
  return SkeletonUtils.clone(gltf.scene);
}

function colorFromHex(hex: string | undefined, fallback: number): THREE.Color {
  try {
    return new THREE.Color(hex || fallback);
  } catch {
    return new THREE.Color(fallback);
  }
}

export function materialFromPreset(preset?: string, tint = 0xb0c4de): THREE.Material {
  switch (preset) {
    case 'lame':
      return makeLameMaterial(tint);
    case 'jacket':
      return makeJacketMaterial();
    case 'breeches':
      return makeBreechesMaterial();
    case 'mask_shell':
      return makeMaskShellMaterial();
    case 'mask_bib':
      return makeMaskBibMaterial(true);
    case 'glove':
      return makeGloveMaterial();
    case 'blade':
      return makeBladeMaterial();
    case 'guard':
      return makeGuardMaterial();
    case 'grip':
      return makeGripMaterial();
    case 'sock':
      return makeSockMaterial();
    case 'shoe':
      return makeShoeMaterial();
    case 'piste_metal':
      return makePisteMetal();
    case 'piste_wood':
      return makePisteWood();
    case 'floor':
      return makeFloorMaterial();
    default:
      return new THREE.MeshStandardMaterial({ color: 0xcccccc, metalness: 0.2, roughness: 0.6 });
  }
}

function makePrimitive(mesh: string | undefined, props: NodeProps): THREE.Object3D {
  const mat = materialFromPreset(props.material?.preset);
  let geom: THREE.BufferGeometry;
  switch (mesh) {
    case 'plane':
      geom = new THREE.PlaneGeometry(2, 2);
      break;
    case 'sphere':
      geom = new THREE.SphereGeometry(0.5, 24, 16);
      break;
    case 'cylinder':
      geom = new THREE.CylinderGeometry(0.35, 0.35, 1, 20);
      break;
    case 'capsule':
      geom = new THREE.CapsuleGeometry(0.3, 0.9, 6, 12);
      break;
    case 'piste': {
      const g = new THREE.Group();
      const wood = new THREE.Mesh(new THREE.BoxGeometry(18, 0.12, 3.2), makePisteWood());
      wood.position.y = 0.06;
      wood.receiveShadow = true;
      const strip = new THREE.Mesh(new THREE.BoxGeometry(14.2, 0.04, 1.6), makePisteMetal());
      strip.position.y = 0.14;
      strip.receiveShadow = true;
      g.add(wood, strip);
      return g;
    }
    case 'box':
    default:
      geom = new THREE.BoxGeometry(1, 1, 1);
      break;
  }
  const m = new THREE.Mesh(geom, mat);
  m.castShadow = props.castShadow !== false;
  m.receiveShadow = props.receiveShadow !== false;
  return m;
}

function applyTransform(obj: THREE.Object3D, node: SceneNodeData): void {
  const t = node.transform;
  obj.position.set(t.position.x, t.position.y, t.position.z);
  obj.rotation.set(
    (t.rotation.x * Math.PI) / 180,
    (t.rotation.y * Math.PI) / 180,
    (t.rotation.z * Math.PI) / 180,
  );
  obj.scale.set(t.scale.x, t.scale.y, t.scale.z);
  obj.visible = node.visible;
  obj.name = node.name;
  obj.userData.sceneNodeId = node.id;
  obj.userData.sceneNodeType = node.type;
}

function makeLight(node: SceneNodeData): THREE.Object3D {
  const p = node.props;
  const color = colorFromHex(p.color, 0xffffff);
  const energy = p.energy ?? 1;
  if (node.type === 'DirectionalLight3D') {
    const l = new THREE.DirectionalLight(color, energy);
    l.castShadow = true;
    const helper = new THREE.DirectionalLightHelper(l, 0.5);
    const g = new THREE.Group();
    g.add(l, helper);
    // default aim down
    l.position.set(0, 0, 0);
    return g;
  }
  if (node.type === 'SpotLight3D') {
    const l = new THREE.SpotLight(color, energy, p.range ?? 12, ((p.angle ?? 35) * Math.PI) / 180, 0.35, 1);
    l.castShadow = true;
    const g = new THREE.Group();
    g.add(l, l.target);
    l.target.position.set(0, -1, 0);
    return g;
  }
  // Omni
  const l = new THREE.PointLight(color, energy, p.range ?? 10, 2);
  const helper = new THREE.PointLightHelper(l, 0.15);
  const g = new THREE.Group();
  g.add(l, helper);
  return g;
}

function makeCamera(node: SceneNodeData): THREE.Object3D {
  const p = node.props;
  const cam = new THREE.PerspectiveCamera(p.fov ?? 45, 1, p.near ?? 0.05, p.far ?? 200);
  const helper = new THREE.CameraHelper(cam);
  helper.visible = true;
  const g = new THREE.Group();
  g.add(cam, helper);
  g.userData.camera = cam;
  g.userData.current = !!p.current;
  return g;
}

function makeCharacterPlaceholder(node: SceneNodeData): THREE.Object3D {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.28, 0.95, 6, 12),
    makeLameMaterial(node.props.colorId === 'red' ? 0xc9a0a0 : 0xb0c4de),
  );
  body.position.y = 0.95;
  body.castShadow = true;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.18, 16, 12), makeMaskShellMaterial());
  head.position.y = 1.72;
  const blade = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 0.9), makeBladeMaterial());
  blade.position.set(0.35, 1.15, 0.45);
  g.add(body, head, blade);
  // facing marker
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.08, 0.2, 8),
    new THREE.MeshStandardMaterial({ color: 0x6ee7ff }),
  );
  nose.rotation.x = Math.PI / 2;
  nose.position.set(0, 1.5, 0.35);
  g.add(nose);
  return g;
}

export class SceneRuntime {
  readonly rootGroup = new THREE.Group();
  readonly tree: SceneTree;
  private links = new Map<string, TreeNodeLink>();
  private scripts: LiveScriptInstance[] = [];
  private playing = false;
  private host: ScriptHost;
  private scene: SceneResource | null = null;
  /** Merged document + packed-instance scripts for play mode */
  private runtimeScripts: SceneResource['scripts'] = {};
  private selectionHelper: THREE.BoxHelper | null = null;
  private selectedId: string | null = null;
  private pendingModelLoads = 0;
  private ownsTree: boolean;
  private areaOverlaps = new Map<string, Set<string>>();
  private animTime = 0;
  private audioCtx: AudioContext | null = null;

  constructor(host: ScriptHost, tree?: SceneTree) {
    this.host = host;
    this.ownsTree = !tree;
    this.tree = tree || new SceneTree(host);
    this.tree.setHost(host);
    this.rootGroup.name = 'SceneRuntimeRoot';
    this.bindTreeHooks();
  }

  private bindTreeHooks(): void {
    this.tree.bindRuntime({
      getNodesInGroup: (group) => {
        const ids = this.tree.get_nodes_in_group_ids(group);
        return ids
          .map((id) => this.links.get(id)?.script?.api)
          .filter(Boolean);
      },
      callGroup: (group, method, args) => {
        const ids = this.tree.get_nodes_in_group_ids(group);
        for (const id of ids) {
          const api = this.links.get(id)?.script?.api as Record<string, unknown> | undefined;
          const fn = api?.[method];
          if (typeof fn === 'function') {
            try {
              (fn as (...a: unknown[]) => void).apply(api, args);
            } catch (e) {
              this.host.log(`call_group ${group}.${method}: ${(e as Error).message}`);
            }
          }
        }
      },
      changeScene: async (path) => {
        const doc = loadSceneByPath(path);
        if (!doc) {
          this.host.log(`change_scene_to_file failed: ${path}`);
          return false;
        }
        const wasPlaying = this.playing;
        await this.build(doc);
        if (wasPlaying) this.startPlay();
        this.tree.scene_changed.emit(path);
        return true;
      },
      onNodeFreed: (id) => {
        this.tree.clear_node_groups(id);
      },
    });
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get isPaused(): boolean {
    return this.tree.is_paused;
  }

  get currentScene(): SceneResource | null {
    return this.scene;
  }

  getNodeObject(id: string): THREE.Object3D | null {
    return this.links.get(id)?.object3d ?? null;
  }

  getNodeLink(id: string): TreeNodeLink | null {
    return this.links.get(id) ?? null;
  }

  getAllLinks(): TreeNodeLink[] {
    return Array.from(this.links.values());
  }

  getScriptCount(): number {
    return this.scripts.length;
  }

  /** Optional external bind logger (editor/game). */
  logBind?: (msg: string) => void;

  findNodeByName(name: string): TreeNodeLink | null {
    for (const link of this.links.values()) {
      if (link.data.name === name) return link;
    }
    return null;
  }

  /** World position of a named marker (for spawn points). */
  getMarkerPosition(name: string): { x: number; y: number; z: number } | null {
    const link = this.findNodeByName(name);
    if (!link) return null;
    const p = link.data.transform.position;
    return { x: p.x, y: p.y, z: p.z };
  }

  /** Advance a single frame while paused (debugger step). */
  stepFrame(dt = 1 / 60): void {
    if (!this.playing) return;
    this.tree.setPaused(false);
    this.update(dt);
    this.tree.setPaused(true);
  }

  async build(scene: SceneResource): Promise<void> {
    this.stopScripts();
    this.clearVisual();
    this.scene = scene;
    this.runtimeScripts = { ...scene.scripts };
    this.rootGroup.name = scene.name;
    this.tree.setCurrentPath(scene.path);
    this.tree.clear_all_groups();
    await this.buildNode(scene.root, null, this.rootGroup);
    this.host.log(`Scene built: ${scene.path} (${this.links.size} nodes)`);
    this.tree.tree_changed.emit();
  }

  private async buildNode(
    data: SceneNodeData,
    parent: TreeNodeLink | null,
    parentObj: THREE.Object3D,
  ): Promise<TreeNodeLink> {
    const obj = await this.createObject(data);
    applyTransform(obj, data);
    parentObj.add(obj);

    const link: TreeNodeLink = {
      data,
      parent,
      children: [],
      object3d: obj,
      script: null,
      signals: new SignalBus(),
    };
    this.links.set(data.id, link);
    if (parent) parent.children.push(link);

    // Register static groups from document
    const groups = (data.props.groups as string[]) || [];
    for (const g of groups) this.tree.add_to_group(data.id, g);

    // Expand PackedScene instances (Godot: instance internal tree at runtime)
    if (data.type === 'PackedScene' && data.props.scenePath) {
      const packed = loadSceneByPath(data.props.scenePath as string);
      if (packed) {
        Object.assign(this.runtimeScripts, structuredClone(packed.scripts));
        const idMap = new Map<string, string>();
        const expanded = rekeyPacked(packed.root, idMap);
        await this.buildNode(expanded, link, obj);
      } else {
        const placeholder = new THREE.Mesh(
          new THREE.BoxGeometry(0.5, 0.5, 0.5),
          new THREE.MeshStandardMaterial({ color: 0x8866ff, wireframe: true }),
        );
        obj.add(placeholder);
      }
    }

    for (const child of data.children) {
      await this.buildNode(child, link, obj);
    }
    return link;
  }

  private async createObject(node: SceneNodeData): Promise<THREE.Object3D> {
    switch (node.type) {
      case 'Node': {
        const g = new THREE.Group();
        return g;
      }
      case 'Node3D':
      case 'Marker3D':
      case 'StaticBody3D': {
        const g = new THREE.Group();
        if (node.type === 'Marker3D') {
          g.add(new THREE.AxesHelper(0.35));
        }
        return g;
      }
      case 'MeshInstance3D': {
        if (node.props.mesh === 'external' && node.props.modelUrl) {
          try {
            this.pendingModelLoads++;
            const model = await loadModel(node.props.modelUrl);
            this.pendingModelLoads--;
            // normalize height roughly
            const box = new THREE.Box3().setFromObject(model);
            const size = box.getSize(new THREE.Vector3());
            if (size.y > 0.01) model.scale.multiplyScalar(1.78 / size.y);
            return model;
          } catch (e) {
            this.pendingModelLoads--;
            this.host.log(`Model load failed: ${(e as Error).message}`);
            return makePrimitive('box', node.props);
          }
        }
        return makePrimitive(node.props.mesh, node.props);
      }
      case 'DirectionalLight3D':
      case 'OmniLight3D':
      case 'SpotLight3D':
        return makeLight(node);
      case 'Camera3D':
        return makeCamera(node);
      case 'CharacterBody3D': {
        if (node.props.modelUrl) {
          try {
            const model = await loadModel(node.props.modelUrl);
            const box = new THREE.Box3().setFromObject(model);
            const size = box.getSize(new THREE.Vector3());
            if (size.y > 0.01) {
              model.scale.multiplyScalar(1.78 / size.y);
              box.setFromObject(model);
              model.position.y = -box.min.y;
            }
            return model;
          } catch {
            return makeCharacterPlaceholder(node);
          }
        }
        return makeCharacterPlaceholder(node);
      }
      case 'PackedScene': {
        // Content expanded in buildNode after the instance shell exists
        const g = new THREE.Group();
        g.userData.packedScene = node.props.scenePath;
        g.userData.isPackedShell = true;
        return g;
      }
      case 'AnimationPlayer': {
        if (!node.props.animations || !Object.keys(node.props.animations as object).length) {
          node.props.animations = defaultAnimLibrary();
        }
        const g = new THREE.Group();
        g.userData.animationPlayer = true;
        g.userData.clip = node.props.autoplay || node.props.defaultClip || 'spin_y';
        g.userData.speed = node.props.animSpeed ?? 1;
        // Small icon in editor
        const marker = new THREE.Mesh(
          new THREE.OctahedronGeometry(0.12),
          new THREE.MeshBasicMaterial({ color: 0x6ee7ff }),
        );
        marker.name = 'AnimPlayerMarker';
        g.add(marker);
        return g;
      }
      case 'AudioStreamPlayer3D': {
        const g = new THREE.Group();
        g.userData.audioPlayer = true;
        g.userData.stream = node.props.stream || 'ui';
        const speaker = new THREE.Mesh(
          new THREE.SphereGeometry(0.1, 12, 8),
          new THREE.MeshBasicMaterial({ color: 0xf0c14b }),
        );
        g.add(speaker);
        return g;
      }
      case 'Timer': {
        const g = new THREE.Group();
        g.userData.timerNode = true;
        g.userData.waitTime = node.props.waitTime ?? 1;
        g.userData.oneShot = node.props.oneShot !== false ? !!node.props.oneShot : false;
        // oneShot default false for repeating - fix:
        g.userData.oneShot = node.props.oneShot === true;
        g.userData.autostart = node.props.autostart !== false;
        g.userData.elapsed = 0;
        g.userData.running = false;
        return g;
      }
      case 'CollisionShape3D': {
        const g = new THREE.Group();
        const size = node.props.shapeSize || { x: 0.5, y: 0.5, z: 0.5 };
        const shape = node.props.shape || 'box';
        let geom: THREE.BufferGeometry;
        if (shape === 'sphere') geom = new THREE.SphereGeometry(Math.max(size.x, size.y, size.z) * 0.5, 16, 12);
        else if (shape === 'capsule')
          geom = new THREE.CapsuleGeometry(Math.max(size.x, size.z) * 0.5, size.y, 4, 8);
        else if (shape === 'cylinder')
          geom = new THREE.CylinderGeometry(size.x * 0.5, size.x * 0.5, size.y, 12);
        else geom = new THREE.BoxGeometry(size.x, size.y, size.z);
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshBasicMaterial({
            color: 0x3dff9a,
            wireframe: true,
            transparent: true,
            opacity: 0.55,
          }),
        );
        g.add(mesh);
        g.userData.collisionShape = true;
        return g;
      }
      case 'Area3D': {
        const g = new THREE.Group();
        g.userData.area3d = true;
        // Default visualization box
        const mesh = new THREE.Mesh(
          new THREE.BoxGeometry(1.2, 1.2, 1.2),
          new THREE.MeshBasicMaterial({
            color: 0x3d9fff,
            wireframe: true,
            transparent: true,
            opacity: 0.4,
          }),
        );
        g.add(mesh);
        return g;
      }
      default:
        return new THREE.Group();
    }
  }

  /** Refresh transforms from document data without full rebuild (inspector edits). */
  syncTransformsFromDocument(): void {
    if (!this.scene) return;
    for (const link of this.links.values()) {
      if (link.object3d) applyTransform(link.object3d, link.data);
    }
    this.refreshSelection();
  }

  select(nodeId: string | null): void {
    this.selectedId = nodeId;
    this.refreshSelection();
  }

  private refreshSelection(): void {
    if (this.selectionHelper) {
      this.selectionHelper.parent?.remove(this.selectionHelper);
      this.selectionHelper = null;
    }
    if (!this.selectedId) return;
    const obj = this.links.get(this.selectedId)?.object3d;
    if (!obj) return;
    try {
      this.selectionHelper = new THREE.BoxHelper(obj, 0x6ee7ff);
      this.rootGroup.add(this.selectionHelper);
    } catch {
      /* empty bounds */
    }
  }

  startPlay(): void {
    if (!this.scene) return;
    this.stopScripts();
    this.playing = true;
    this.tree.setPaused(false);
    this.tree.setRunning(true);

    const onQueueFree = (id: string) => {
      const link = this.links.get(id);
      if (!link) return;
      if (link.script?.entered) callLifecycle(link.script, '_exit_tree');
      link.object3d?.parent?.remove(link.object3d);
      this.tree.clear_node_groups(id);
      this.links.delete(id);
      this.scripts = this.scripts.filter((s) => s.nodeId !== id);
    };

    const globals = {
      Input,
      Engine: {
        get_singleton: (name: string) => this.tree.get_singleton(name),
        get_main_loop: () => this.tree.createTreeAPI(),
      },
      print: (...a: unknown[]) => this.host.log(a.map(String).join(' ')),
    };

    for (const link of this.links.values()) {
      const sid = link.data.scriptId;
      if (!sid || !this.runtimeScripts[sid]) continue;
      const src = this.runtimeScripts[sid].source;
      try {
        const handlers = compileScript(src, this.host, globals);
        const signalNames = parseSignals(src);
        const inst = bindScriptInstance(
          link,
          handlers,
          link.data.scriptProps || {},
          this.host,
          this.tree,
          onQueueFree,
          signalNames,
        );
        // Full link map for get_node
        inst.api = createNodeAPI(link, this.links, this.host, this.tree, onQueueFree);
        for (const [k, v] of Object.entries(handlers)) {
          if (typeof v !== 'function' && !k.startsWith('_')) inst.api[k] = v;
        }
        for (const [k, v] of Object.entries(link.data.scriptProps || {})) {
          inst.api[k] = v;
        }
        this.scripts.push(inst);
      } catch (e) {
        this.host.log(`Script error on ${link.data.name}: ${(e as Error).message}`);
      }
    }

    const enter = (link: TreeNodeLink) => {
      if (link.script && !link.script.entered) {
        callLifecycle(link.script, '_enter_tree');
        link.script.entered = true;
        if (link.script.error) this.host.log(`_enter_tree error [${link.data.name}]: ${link.script.error}`);
      }
      for (const c of link.children) enter(c);
    };
    const rootLink = this.links.get(this.scene.root.id);
    if (rootLink) enter(rootLink);

    const ready = (link: TreeNodeLink) => {
      for (const c of link.children) ready(c);
      if (link.script && !link.script.ready) {
        callLifecycle(link.script, '_ready');
        link.script.ready = true;
        if (link.script.error) this.host.log(`_ready error [${link.data.name}]: ${link.script.error}`);
      }
    };
    if (rootLink) ready(rootLink);

    // Built-in node systems: Timer autostart, Audio autoplay, AnimationPlayer
    this.animTime = 0;
    this.areaOverlaps.clear();
    for (const link of this.links.values()) {
      // Ensure Area3D has signals
      if (link.data.type === 'Area3D') {
        link.signals.signal('body_entered');
        link.signals.signal('body_exited');
      }
      if (link.data.type === 'Timer') {
        link.signals.signal('timeout');
        const o = link.object3d;
        if (o) {
          o.userData.elapsed = 0;
          o.userData.running = link.data.props.autostart !== false;
          o.userData.waitTime = link.data.props.waitTime ?? 1;
          o.userData.oneShot = link.data.props.oneShot === true;
        }
      }
      if (link.data.type === 'AudioStreamPlayer3D' && link.data.props.autoplayAudio) {
        this.playNodeAudio(link);
      }
    }

    this.host.log(
      `▶ Playing scene "${this.scene.name}" — ${this.scripts.length} script(s) · InputMap · areas/timers · physics ${this.tree.createTreeAPI().physics_ticks_per_second} Hz`,
    );
  }

  private playNodeAudio(link: TreeNodeLink): void {
    const stream = (link.data.props.stream as string) || 'ui';
    const volDb = (link.data.props.volumeDb as number) || 0;
    const pitch = (link.data.props.pitchScale as number) || 1;
    const gain = Math.pow(10, volDb / 20) * 0.2;
    try {
      if (!this.audioCtx) this.audioCtx = new AudioContext();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') void ctx.resume();
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const freqs: Record<string, number> = {
        lunge: 520,
        parry: 880,
        touch: 660,
        allez: 440,
        en_garde: 220,
        ui: 600,
        tone: 440,
      };
      osc.frequency.value = (freqs[stream] || 500) * pitch;
      osc.type = stream === 'parry' ? 'square' : stream === 'lunge' ? 'sawtooth' : 'sine';
      g.gain.value = gain;
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch {
      this.host.log(`Audio play failed: ${stream}`);
    }
  }

  /** Resolve relative path from AnimationPlayer link. */
  private resolveAnimTarget(from: TreeNodeLink, path: string): TreeNodeLink | null {
    if (!path || path === '.' || path === 'self') return from;
    if (path === '..' || path === 'parent') return from.parent;
    // Absolute-ish from scene root
    if (path.startsWith('/')) {
      const parts = path.split('/').filter(Boolean);
      let cur: TreeNodeLink | null = this.links.get(this.scene?.root.id || '') || null;
      // walk from root
      if (!cur) {
        for (const l of this.links.values()) {
          if (!l.parent) {
            cur = l;
            break;
          }
        }
      }
      let i = 0;
      if (cur && parts[0] === cur.data.name) i = 1;
      for (; i < parts.length; i++) {
        if (!cur) return null;
        cur = cur.children.find((c) => c.data.name === parts[i]) ?? null;
      }
      return cur;
    }
    // Relative child path
    const parts = path.split('/').filter(Boolean);
    let cur: TreeNodeLink | null = from;
    for (const p of parts) {
      if (!cur) return null;
      if (p === '.') continue;
      if (p === '..') {
        cur = cur.parent;
        continue;
      }
      cur = cur.children.find((c) => c.data.name === p) ?? null;
    }
    return cur;
  }

  private applyAnimProperty(link: TreeNodeLink, property: AnimProperty, value: number): void {
    const t = link.data.transform;
    switch (property) {
      case 'position.x':
        t.position.x = value;
        break;
      case 'position.y':
        t.position.y = value;
        break;
      case 'position.z':
        t.position.z = value;
        break;
      case 'rotation.x':
        t.rotation.x = value;
        break;
      case 'rotation.y':
        t.rotation.y = value;
        break;
      case 'rotation.z':
        t.rotation.z = value;
        break;
      case 'scale.x':
        t.scale.x = value;
        break;
      case 'scale.y':
        t.scale.y = value;
        break;
      case 'scale.z':
        t.scale.z = value;
        break;
    }
    if (link.object3d) applyTransform(link.object3d, link.data);
  }

  /** Drive AnimationPlayer — custom tracks first, then procedural builtins. */
  private updateAnimationPlayers(dt: number): void {
    this.animTime += dt;
    for (const link of this.links.values()) {
      if (link.data.type !== 'AnimationPlayer') continue;
      if (link.data.props.animPlaying === false) continue;
      const clipName = (link.data.props.autoplay || link.data.props.defaultClip || 'spin_y') as string;
      if (clipName === 'none') continue;
      const speed = (link.data.props.animSpeed as number) ?? 1;
      const lib = parseAnimLibrary(link.data.props.animations) as AnimLibrary;
      const clip: AnimClipData | undefined = lib[clipName];
      // Local playhead on node
      let localT = (link.data.props.animTime as number) || 0;
      localT += dt * speed;
      if (clip) {
        if (clip.loop && clip.length > 0) localT = localT % clip.length;
        else localT = Math.min(localT, clip.length);
        link.data.props.animTime = localT;
        for (const track of clip.tracks) {
          const v = sampleTrack(track, localT, clip.length, clip.loop);
          if (v === null) continue;
          const target = this.resolveAnimTarget(link, track.path);
          if (!target) continue;
          this.applyAnimProperty(target, track.property, v);
        }
      } else {
        // Procedural fallbacks
        link.data.props.animTime = localT;
        const parent = link.parent;
        if (!parent?.object3d || !parent.data) continue;
        const t = localT;
        if (clipName === 'spin_y') {
          parent.data.transform.rotation.y = (t * 180) % 360;
        } else if (clipName === 'bob') {
          if (parent.object3d.userData.bobBaseY === undefined) {
            parent.object3d.userData.bobBaseY = parent.data.transform.position.y;
          }
          parent.data.transform.position.y =
            parent.object3d.userData.bobBaseY + Math.sin(t * 3) * 0.15;
        } else if (clipName === 'pulse') {
          const s = 1 + Math.sin(t * 4) * 0.08;
          parent.data.transform.scale = { x: s, y: s, z: s };
        }
        applyTransform(parent.object3d, parent.data);
      }
    }
  }

  /** Preview an AnimationPlayer clip at time t (editor scrub). */
  previewAnimation(nodeId: string, clipName: string, time: number): void {
    const link = this.links.get(nodeId);
    if (!link || link.data.type !== 'AnimationPlayer') return;
    const lib = parseAnimLibrary(link.data.props.animations);
    const clip = lib[clipName];
    if (!clip) return;
    for (const track of clip.tracks) {
      const v = sampleTrack(track, time, clip.length, clip.loop);
      if (v === null) continue;
      const target = this.resolveAnimTarget(link, track.path);
      if (target) this.applyAnimProperty(target, track.property, v);
    }
  }

  private updateTimers(dt: number): void {
    for (const link of this.links.values()) {
      if (link.data.type !== 'Timer') continue;
      const o = link.object3d;
      if (!o || !o.userData.running) continue;
      o.userData.elapsed += dt;
      const wait = o.userData.waitTime ?? 1;
      if (o.userData.elapsed >= wait) {
        o.userData.elapsed = 0;
        // Emit timeout on script API or signal bus
        try {
          link.script?.api.emit_signal('timeout');
          if (!link.script) link.signals.signal('timeout').emit();
        } catch {
          /* */
        }
        if (o.userData.oneShot) o.userData.running = false;
      }
    }
  }

  stopScripts(): void {
    for (const s of this.scripts) {
      if (s.entered) callLifecycle(s, '_exit_tree');
    }
    this.scripts = [];
    this.playing = false;
    this.tree.setRunning(false);
    this.tree.setPaused(false);
  }

  togglePause(): void {
    if (!this.playing) return;
    this.tree.setPaused(!this.tree.is_paused);
  }

  update(dt: number): void {
    if (this.selectionHelper) this.selectionHelper.update();
    if (!this.playing) return;

    const frame = this.tree.begin_frame(dt);
    if (!frame.process) {
      this.tree.end_frame();
      return;
    }

    // Built-in systems
    this.updateTimers(dt);
    this.updateAnimationPlayers(dt);

    // Idle process
    for (const s of this.scripts) {
      const link = this.links.get(s.nodeId);
      if (!link) continue;
      if (link.data.processMode === 'disabled') continue;
      callProcess(s, '_process', dt);
      if (s.error) {
        this.host.log(`process error [${link.data.name}]: ${s.error}`);
        s.error = null;
      }
    }

    // Fixed physics steps + area overlaps
    for (let i = 0; i < frame.physicsSteps; i++) {
      this.tree.emit_physics_frame();
      for (const s of this.scripts) {
        const link = this.links.get(s.nodeId);
        if (!link) continue;
        if (link.data.processMode === 'disabled') continue;
        callProcess(s, '_physics_process', frame.physicsDt);
        if (s.error) {
          this.host.log(`physics error [${link.data.name}]: ${s.error}`);
          s.error = null;
        }
      }
      const colliders = collectColliders(this.links);
      resolveStaticCollisions(colliders);
      this.areaOverlaps = updateAreaOverlaps(colliders, this.areaOverlaps);
    }

    this.tree.end_frame();
  }

  private clearVisual(): void {
    this.stopScripts();
    while (this.rootGroup.children.length) {
      this.rootGroup.remove(this.rootGroup.children[0]);
    }
    this.links.clear();
    this.selectionHelper = null;
    this.selectedId = null;
    this.scene = null;
    this.runtimeScripts = {};
    this.tree.clear_all_groups();
  }

  clear(): void {
    this.clearVisual();
  }

  dispose(): void {
    this.clear();
    if (this.ownsTree) this.tree.dispose();
  }
}
